/**
 * One filter model for the three bill lists — Bills, Bulk Bill Run and Bulk
 * Customer Report (spec 2026-10-19). The adviser chasing one retailer's
 * mismatches moves between all three; they used to filter differently.
 *
 * The value lives in the URL query string (`filtersFromParams` /
 * `filtersToParams`), by key, never by label, so a filtered view can be shared
 * and survives a refresh. The predicate is pure: each screen supplies what it
 * derived for the bill (`BillFilterContext`) and every screen narrows the same
 * way. React-free so vitest (node) can cover it.
 */
import type { BvBill } from '../types/billTypes';
import { isArchived } from '../utils/archiveScope';
import { STATUS_ORDER, type BillStatus } from '../utils/billStatus';
import { billInMonthRange, billMonthOptions, type MonthOption } from './billMonths';
import { WORKFLOW_ORDER, type BillWorkflowStatus } from './billWorkflow';
import type { ReportBucket, ReportSendStatus } from './reportStatus';
import { matchesSiteFilters, siteFilterOptions, siteValue, type SiteFilterKey } from './siteFilters';

export type BillFilters = {
  /** NMI or entity (client) name, case-insensitive. */
  q: string;
  /** Billing months, `YYYY-MM` or '' for an open end (lib/billMonths). */
  from: string;
  to: string;
  entity: string;
  retailer: string;
  distributor: string;
  state: string;
  tariff: string;
  /** utils/billStatus — the Bills list's verdict, a superset of the report buckets. */
  verdict: BillStatus | '';
  /** lib/billWorkflow. */
  status: BillWorkflowStatus | '';
  /** lib/reportStatus. */
  send: ReportSendStatus | '';
};

/** The URL keys, in the order they are written. */
export const BILL_FILTER_KEYS: (keyof BillFilters)[] = [
  'q', 'from', 'to', 'entity', 'retailer', 'distributor', 'state', 'tariff', 'verdict', 'status', 'send',
];

export const NO_BILL_FILTERS: BillFilters = {
  q: '', from: '', to: '', entity: '', retailer: '', distributor: '', state: '', tariff: '',
  verdict: '', status: '', send: '',
};

/** Send statuses in the order the filter lists them. */
export const SEND_STATUS_ORDER: ReportSendStatus[] = ['not_sent', 'send_failed', 'sent', 'changed_since_sent', 'no_report'];

/** Display labels only; the key (`matched`) is what the URL and the predicate use. */
export const VERDICT_LABELS: Record<BillStatus, string> = {
  overcharge: 'Overcharge',
  undercharge: 'Undercharge',
  matched: 'Within range',
  warnings: 'Warnings',
  calculated: 'Calculated',
  pending: 'Pending',
  archived: 'Archived',
};

/** Every verdict but Archived — the lists that load active bills only. */
export const ACTIVE_VERDICTS: BillStatus[] = STATUS_ORDER.filter((s) => s !== 'archived');

/** A Bulk Customer Report result card, as the shared verdict. */
export const BUCKET_VERDICT: Record<ReportBucket, BillStatus> = {
  overcharge: 'overcharge',
  undercharge: 'undercharge',
  within_range: 'matched',
};

/** What a screen derived for the bill. Null: not derived (e.g. its load failed). */
export type BillFilterContext = {
  verdict: BillStatus | null;
  workflow: BillWorkflowStatus | null;
  sendStatus: ReportSendStatus | null;
};

export type BillFilterSubject = Pick<BvBill, 'site' | 'bill_from' | 'bill_to'> & { archived_at?: string | null };

/** True when the bill passes every filter that is set. */
export function matchesBillFilters(bill: BillFilterSubject, ctx: BillFilterContext, filters: BillFilters): boolean {
  // Job 81: an archived bill is retired — reachable only by asking for it.
  if (filters.verdict !== 'archived' && isArchived(bill)) return false;
  const q = filters.q.trim().toLowerCase();
  if (q && !(
    (bill.site?.nmi ?? '').toLowerCase().includes(q)
    || siteValue(bill, 'entity').toLowerCase().includes(q)
  )) return false;
  if (!billInMonthRange(bill, filters.from || null, filters.to || null)) return false;
  if (!matchesSiteFilters(bill, filters)) return false;
  if (filters.verdict && ctx.verdict !== filters.verdict) return false;
  if (filters.status && ctx.workflow !== filters.status) return false;
  if (filters.send && ctx.sendStatus !== filters.send) return false;
  return true;
}

const MONTH_KEY = /^\d{4}-(0[1-9]|1[0-2])$/;

function oneOf<T extends string>(allowed: readonly T[], value: string | null): T | '' {
  return value && (allowed as readonly string[]).includes(value) ? (value as T) : '';
}

/** The filters a query string states; anything unrecognised is dropped. */
export function filtersFromParams(params: URLSearchParams): BillFilters {
  const text = (key: string) => (params.get(key) ?? '').trim();
  const month = (key: string) => (MONTH_KEY.test(text(key)) ? text(key) : '');
  return {
    q: params.get('q') ?? '',
    from: month('from'),
    to: month('to'),
    entity: text('entity'),
    retailer: text('retailer'),
    distributor: text('distributor'),
    state: text('state'),
    tariff: text('tariff'),
    verdict: oneOf(STATUS_ORDER, params.get('verdict')),
    status: oneOf(WORKFLOW_ORDER, params.get('status')),
    send: oneOf(SEND_STATUS_ORDER, params.get('send')),
  };
}

/** `base` with the filter keys set or removed; every other param kept. */
export function filtersToParams(filters: BillFilters, base: URLSearchParams = new URLSearchParams()): URLSearchParams {
  const next = new URLSearchParams(base);
  for (const key of BILL_FILTER_KEYS) {
    const value = filters[key];
    if (value) next.set(key, value);
    else next.delete(key);
  }
  return next;
}

/**
 * One URL write: a filter patch plus screen-specific params ('' removes one).
 * react-router computes a functional update from the params of the render it
 * was called in, so a page that made two writes in one handler would lose the
 * first — this is the only write the pages make.
 */
export function applyFilterUpdate(
  prev: URLSearchParams,
  patch: Partial<BillFilters>,
  extras: Record<string, string> = {},
): URLSearchParams {
  const next = filtersToParams({ ...filtersFromParams(prev), ...patch }, prev);
  for (const [name, value] of Object.entries(extras)) {
    if (value) next.set(name, value);
    else next.delete(name);
  }
  return next;
}

export function hasActiveBillFilters(filters: BillFilters): boolean {
  return BILL_FILTER_KEYS.some((key) => (key === 'q' ? filters.q.trim() !== '' : filters[key] !== ''));
}

/** Just the shared filters of a query, as a query string (no `?`). */
export function sharedFilterQuery(params: URLSearchParams): string {
  return filtersToParams(filtersFromParams(params)).toString();
}

/**
 * The saved filters to put on a screen that opened with an EMPTY query — the
 * sidebar links carry none. An explicit link (`?run=`, a shared URL) is never
 * overridden. Null when there is nothing to restore.
 */
export function restoredParams(current: URLSearchParams, saved: string | null): URLSearchParams | null {
  if (current.toString() !== '' || !saved) return null;
  const restored = filtersToParams(filtersFromParams(new URLSearchParams(saved)));
  return restored.toString() ? restored : null;
}

/** sessionStorage key for the saved filters, per signed-in user; null (no storage) without one. */
export function savedFiltersKey(userId: string | null | undefined): string | null {
  return userId ? `bv.billFilters:${userId}` : null;
}

export type BillFilterOptions = {
  months: MonthOption[];
  site: Record<SiteFilterKey, string[]>;
};

/** Dropdown options from the loaded bills, so every option matches at least one. */
export function billFilterOptions(bills: Pick<BvBill, 'site' | 'bill_from' | 'bill_to'>[]): BillFilterOptions {
  return { months: billMonthOptions(bills), site: siteFilterOptions(bills) };
}
